import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Award, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { ProfPortrait } from "@/components/ProfPortrait";

const CTA_PRIMARY =
  "inline-flex items-center gap-2 rounded-full bg-[#E8F1EC] px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#0D3327] transition-colors duration-300 hover:bg-white";

const TIMELINE = [
  { year: "1979", text: "Laurea con lode in Medicina e Chirurgia presso l'Università degli Studi di Genova." },
  { year: "1983", text: "Specializzazione in Oftalmologia presso l'Università degli Studi di Genova." },
  { year: "1986", text: "Specializzazione in Chirurgia Oculare presso l'Università di Genova." },
];

const INTERNAZIONALE = [
  {
    luogo: "Wills Eye Hospital — Philadelphia, USA",
    text: "Due anni di perfezionamento e formazione specialistica presso uno dei centri oftalmologici più prestigiosi al mondo.",
  },
  {
    luogo: "King Khaled Eye Specialist Hospital — Riyadh, Arabia Saudita",
    text: "Senior Staff per 3,5 anni, con particolare attività nell'ambito dei casi complessi di chirurgia del segmento anteriore.",
  },
];

const SOCIETA = [
  { text: "Presidente dell'alleanza tra società scientifiche oftalmologiche europee EUEYE." },
  { text: "Chairman della Fondazione della European Glaucoma Society – EGS Foundation." },
  { text: "Direttore Medico della Fondazione Banca degli Occhi Melvin Jones di Genova dal 2001 al 2024." },
  { text: "Membro del Consiglio della Fondazione Istituto David Chiossone di Genova." },
  { text: "Membro dell'American Ophthalmological Society dal 2017." },
];

const PREMI = [
  "Premio della Società Oftalmologica Italiana per la ricerca clinica.",
  "Premio AIRCMO per la ricerca in oftalmologia sulle cellule staminali corneali.",
  "Honor Award – American Academy of Ophthalmology.",
  "Senior Achievement Award – American Academy of Ophthalmology.",
  "Silver Fellowship – ARVO.",
];

export default function ChiSono() {
  return (
    <div data-testid="about-page">
      <PageHero
        testid="about-section"
        eyebrow="Chi sono"
        title="Un percorso dedicato all'oftalmologia"
        tagline="Oculista esperto a Genova e Savona"
        subtitle="Clinica, chirurgia, ricerca e formazione: oltre quattro decenni dedicati alla salute dell'occhio."
      />

      {/* INTRODUZIONE */}
      <section className="bg-[#F8FAF8]">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16 lg:px-8">
          <Reveal>
            <div className="relative mx-auto w-full max-w-sm">
              <div className="absolute -inset-3 rounded-md border border-[#D5E2DB]" />
              <ProfPortrait testid="about-portrait-image" className="aspect-[4/5] w-full rounded-md" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-5 text-base leading-relaxed text-[#4B5852] sm:text-lg">
              <p className="font-serif text-xl leading-relaxed text-[#0D3327] sm:text-2xl">
                "Ho dedicato la mia attività professionale alla clinica, alla chirurgia, alla ricerca e alla formazione in Oftalmologia, con particolare interesse per il segmento anteriore dell'occhio: cataratta, glaucoma, cornea e chirurgia refrattiva."
              </p>
              <p>
                Il mio percorso professionale si è sviluppato tra Genova e importanti centri oftalmologici internazionali, mantenendo sempre uno stretto legame tra attività clinica, ricerca scientifica e insegnamento.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORMAZIONE E PERCORSO INTERNAZIONALE */}
      <section data-testid="timeline-section" className="border-y border-[#D5E2DB] bg-white">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <GraduationCap size={20} className="text-[#2E6B50]" />
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#2E6B50]">
                Formazione e percorso internazionale
              </p>
            </div>
          </Reveal>
          <div data-testid="timeline-container" className="mt-12 space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div data-testid={`timeline-item-${i}`} className={`relative flex gap-6 sm:gap-10 ${i < TIMELINE.length - 1 ? "pb-6" : ""}`}>
                  {i < TIMELINE.length - 1 && (
                    <div className="absolute left-[7px] top-6 h-full w-px bg-[#D5E2DB]" />
                  )}
                  <div className="mt-1.5 h-[15px] w-[15px] shrink-0 rounded-full border-2 border-[#174C3A] bg-white" />
                  <div>
                    <p className="font-mono text-sm font-medium text-[#174C3A]">{t.year}</p>
                    <p className="mt-1 text-base leading-relaxed text-[#1A221E]">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div data-testid="international-subsection" className="mt-12 border-t border-[#D5E2DB] pt-10">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2E6B50]">
                Esperienza internazionale
              </p>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {INTERNAZIONALE.map((e, i) => (
                  <div
                    key={e.luogo}
                    data-testid={`international-item-${i}`}
                    className="h-full rounded-lg border border-[#D5E2DB] bg-[#F8FAF8] p-6 transition-all duration-300 hover:border-[#2E6B50]/50"
                  >
                    <p className="font-serif text-lg font-semibold leading-snug text-[#0D3327]">{e.luogo}</p>
                    <p className="mt-3 text-sm leading-relaxed text-[#4B5852]">{e.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* UNIVERSITÀ E POLICLINICO */}
      <section className="bg-[#E8F1EC]">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#174C3A]">
              Università e Policlinico San Martino
            </p>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-[#1A221E] sm:text-lg">
              <p>
                Dal 2009 al 2024 il Professor Traverso ha diretto la U.O.C. Clinica Oculistica dell'Ospedale Policlinico San Martino di Genova e la Scuola di Specializzazione in Oftalmologia dell'Università di Genova. Dal 2010 al 2024 è stato Professore Ordinario di Oftalmologia.
              </p>
              <p>
                Nel biennio accademico 2025–2026 prosegue l'attività di docenza nel Corso di Laurea in Ortottica e nella Scuola di Specializzazione in Oftalmologia.
              </p>
              <p>
                Durante la sua direzione, la Clinica Oculistica ha ottenuto il riconoscimento della Commissione Europea come sito di riferimento EIP-AHA ed è entrata nella rete europea dedicata alle malattie rare in oftalmologia ERN-EYE.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RICERCA */}
      <section className="bg-[#F8FAF8]">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold leading-tight text-[#0D3327] sm:text-4xl">
              Ricerca e innovazione
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-[#4B5852] sm:text-lg">
              <p>
                L'attività scientifica del Professor Traverso si è concentrata prevalentemente sul segmento anteriore dell'occhio, con particolare attenzione al glaucoma e alle patologie corneali.
              </p>
              <p>
                Tra le attività scientificamente più rilevanti rientrano gli studi sulla terapia cellulare corneale e sul trapianto di cellule staminali limbari, che hanno contribuito alle prime dimostrazioni cliniche dell'utilizzo di queste tecniche nell'uomo.
              </p>
              <p>
                I risultati di questo percorso di ricerca sono stati oggetto di pubblicazioni scientifiche internazionali, incluse pubblicazioni su The Lancet.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SOCIETÀ SCIENTIFICHE */}
      <section className="border-y border-[#D5E2DB] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <Users size={20} className="text-[#2E6B50]" />
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#2E6B50]">
                Società scientifiche e incarichi
              </p>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SOCIETA.map((s, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`society-card-${i}`}
                  className="h-full rounded-lg border border-[#D5E2DB] bg-[#F8FAF8] p-7 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#2E6B50]/50 hover:shadow-md"
                >
                  <div className="h-1 w-10 rounded-full bg-[#174C3A]" />
                  <p className="mt-5 text-sm leading-relaxed text-[#1A221E]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RICONOSCIMENTI */}
      <section className="bg-[#F8FAF8]">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <Award size={20} className="text-[#2E6B50]" />
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#2E6B50]">Riconoscimenti</p>
            </div>
          </Reveal>
          <div className="mt-10 space-y-4">
            {PREMI.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div
                  data-testid={`award-item-${i}`}
                  className="flex items-start gap-4 rounded-lg border border-[#D5E2DB] bg-white p-5 transition-all duration-300 hover:border-[#2E6B50]/50"
                >
                  <Award size={18} className="mt-0.5 shrink-0 text-[#174C3A]" />
                  <p className="text-base leading-relaxed text-[#1A221E]">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ATTIVITÀ OGGI */}
      <section data-testid="about-today-cta" className="bg-[#0D3327]">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-24 lg:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold leading-tight text-white sm:text-4xl">
              L'attività clinica oggi
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
              Oggi il Professor Carlo Enrico Traverso svolge attività specialistica e chirurgica privata a Genova e Savona, occupandosi in particolare di cataratta, chirurgia refrattiva, glaucoma, cornea e valutazioni oftalmologiche specialistiche.
            </p>
            <Link to="/richiedi-una-valutazione" data-testid="about-cta-evaluation" className={`${CTA_PRIMARY} mt-9`}>
              Richiedi una valutazione <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
